// Generated from legacy db.ts to keep files under 500 lines.
import { sql } from "../../base";
import type { MatterDeadline } from "../../types";

const deadlinesModule = {
  async getUpcoming(userId: string, days: number = 30): Promise<MatterDeadline[]> {
    try {
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + days);
      endDate.setHours(23, 59, 59, 999);
      
      // Get deadlines from all tables that have deadline fields
      const result = await sql`
        (
          -- Matter deadlines from matter_deadlines table
          SELECT 
            md.id,
            md.matter_id,
            md.user_id,
            md.title,
            md.description,
            md.due_date,
            md.reminder_date,
            md.is_completed,
            md.completed_at,
            md.created_at,
            md.updated_at,
            m.title as matter_name,
            m.client_id,
            c.name as client_name,
            'matter_deadline' as deadline_type
          FROM matter_deadlines md
          JOIN matters m ON m.id = md.matter_id
          LEFT JOIN clients c ON c.id = m.client_id
          WHERE md.user_id = ${userId}
          AND md.due_date <= ${endDate}
          AND md.is_completed = false
        )
        UNION ALL
        (
          -- Task deadlines
          SELECT 
            t.id,
            t.goal_id as matter_id,
            t.user_id,
            t.title,
            t.description,
            t.deadline as due_date,
            NULL as reminder_date,
            CASE WHEN t.status = 'Complete' THEN true ELSE false END as is_completed,
            t.end_date as completed_at,
            t.created_at,
            t.updated_at,
            COALESCE(g.title, 'No Deliverable') as matter_name,
            NULL as client_id,
            NULL as client_name,
            'task' as deadline_type
          FROM tasks t
          LEFT JOIN goals g ON g.id = t.goal_id
          WHERE t.user_id = ${userId}
          AND t.deadline IS NOT NULL
          AND t.deadline <= ${endDate}
          AND t.status != 'Complete'
          AND (t.is_regular = false OR t.is_regular IS NULL)
        )
        UNION ALL
        (
          -- Goal/Deliverable deadlines (only if they have incomplete tasks)
          SELECT 
            g.id,
            g.matter_id,
            g.user_id,
            g.title,
            g.description,
            g.deadline as due_date,
            NULL as reminder_date,
            false as is_completed,
            NULL as completed_at,
            g.created_at,
            g.updated_at,
            COALESCE(m.title, p.title, 'No Matter/Project') as matter_name,
            COALESCE(m.client_id, p.client_id) as client_id,
            c.name as client_name,
            'deliverable' as deadline_type
          FROM goals g
          LEFT JOIN matters m ON m.id = g.matter_id
          LEFT JOIN projects p ON p.id = g.project_id
          LEFT JOIN clients c ON c.id = COALESCE(m.client_id, p.client_id, g.client_id)
          WHERE g.user_id = ${userId}
          AND g.deadline IS NOT NULL
          AND g.deadline <= ${endDate}
          AND g.archived = false
          AND (g.is_complete = false OR g.is_complete IS NULL)
          -- Only show deliverables that have incomplete tasks
          AND EXISTS (
            SELECT 1 FROM tasks t 
            WHERE t.goal_id = g.id 
            AND t.status != 'Complete'
          )
        )
        UNION ALL
        (
          -- Project deadlines (only if they have incomplete deliverables with incomplete tasks)
          SELECT 
            p.id,
            NULL as matter_id,
            p.user_id,
            p.title,
            p.description,
            p.deadline as due_date,
            NULL as reminder_date,
            false as is_completed,
            NULL as completed_at,
            p.created_at,
            p.updated_at,
            'Project' as matter_name,
            p.client_id,
            c.name as client_name,
            'project' as deadline_type
          FROM projects p
          LEFT JOIN clients c ON c.id = p.client_id
          WHERE p.user_id = ${userId}
          AND p.deadline IS NOT NULL
          AND p.deadline <= ${endDate}
          AND p.is_archived = false
          AND (p.is_complete = false OR p.is_complete IS NULL)
          -- Only show projects that have deliverables with incomplete tasks
          AND EXISTS (
            SELECT 1 FROM goals g
            JOIN tasks t ON t.goal_id = g.id
            WHERE g.project_id = p.id 
            AND g.archived = false
            AND (g.is_complete = false OR g.is_complete IS NULL)
            AND t.status != 'Complete'
          )
        )
        UNION ALL
        (
          -- Matter deadlines from matters table (only if they have incomplete deliverables with incomplete tasks)
          SELECT 
            m.id,
            m.id as matter_id,
            m.user_id,
            m.title,
            m.description,
            m.deadline as due_date,
            NULL as reminder_date,
            false as is_completed,
            NULL as completed_at,
            m.created_at,
            m.updated_at,
            m.title as matter_name,
            m.client_id,
            c.name as client_name,
            'matter' as deadline_type
          FROM matters m
          LEFT JOIN clients c ON c.id = m.client_id
          WHERE m.user_id = ${userId}
          AND m.deadline IS NOT NULL
          AND m.deadline <= ${endDate}
          AND m.is_archived = false
          AND (m.is_complete = false OR m.is_complete IS NULL)
          -- Only show matters that have deliverables with incomplete tasks
          AND EXISTS (
            SELECT 1 FROM goals g
            JOIN tasks t ON t.goal_id = g.id
            WHERE g.matter_id = m.id 
            AND g.archived = false
            AND (g.is_complete = false OR g.is_complete IS NULL)
            AND t.status != 'Complete'
          )
        )
        ORDER BY due_date ASC
      `;
      
      return (result || []) as MatterDeadline[];
    } catch (error) {
      console.error('Error fetching upcoming deadlines:', error);
      return [];
    }
  },

  async getOverdue(userId: string): Promise<MatterDeadline[]> {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const result = await sql`
        SELECT 
          md.*,
          m.title as matter_name,
          m.client_id,
          c.name as client_name
        FROM matter_deadlines md
        JOIN matters m ON m.id = md.matter_id
        LEFT JOIN clients c ON c.id = m.client_id
        WHERE md.user_id = ${userId}
        AND md.due_date < ${today}
        AND md.is_completed = false
        ORDER BY md.due_date ASC
      `;
      
      return (result || []) as MatterDeadline[];
    } catch (error) {
      console.error('Error fetching overdue deadlines:', error);
      return [];
    }
  },

  async getToday(userId: string): Promise<MatterDeadline[]> {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const result = await sql`
        SELECT 
          md.*,
          m.title as matter_name,
          m.client_id,
          c.name as client_name
        FROM matter_deadlines md
        JOIN matters m ON m.id = md.matter_id
        LEFT JOIN clients c ON c.id = m.client_id
        WHERE md.user_id = ${userId}
        AND md.due_date >= ${today}
        AND md.due_date < ${tomorrow}
        AND md.is_completed = false
        ORDER BY md.due_date ASC
      `;
      
      return (result || []) as MatterDeadline[];
    } catch (error) {
      console.error('Error fetching today\'s deadlines:', error);
      return [];
    }
  },

  async getThisWeek(userId: string): Promise<MatterDeadline[]> {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Get end of week (Sunday)
      const endOfWeek = new Date(today);
      const daysUntilSunday = 7 - today.getDay();
      endOfWeek.setDate(today.getDate() + daysUntilSunday);
      endOfWeek.setHours(23, 59, 59, 999);
      
      const result = await sql`
        SELECT 
          md.*,
          m.title as matter_name,
          m.client_id,
          c.name as client_name
        FROM matter_deadlines md
        JOIN matters m ON m.id = md.matter_id
        LEFT JOIN clients c ON c.id = m.client_id
        WHERE md.user_id = ${userId}
        AND md.due_date >= ${today}
        AND md.due_date <= ${endOfWeek}
        AND md.is_completed = false
        ORDER BY md.due_date ASC
      `;
      
      return (result || []) as MatterDeadline[];
    } catch (error) {
      console.error('Error fetching this week\'s deadlines:', error);
      return [];
    }
  },

  async getByDateRange(userId: string, startDate: Date, endDate: Date): Promise<MatterDeadline[]> {
    try {
      const result = await sql`
        SELECT 
          md.*,
          m.title as matter_name,
          m.client_id,
          c.name as client_name
        FROM matter_deadlines md
        JOIN matters m ON m.id = md.matter_id
        LEFT JOIN clients c ON c.id = m.client_id
        WHERE md.user_id = ${userId}
        AND md.due_date >= ${startDate}
        AND md.due_date <= ${endDate}
        ORDER BY md.due_date ASC
      `;
      
      return (result || []) as MatterDeadline[];
    } catch (error) {
      console.error('Error fetching deadlines by date range:', error);
      return [];
    }
  },

  async getStats(userId: string): Promise<{ overdue: number; today: number; thisWeek: number; upcoming30Days: number }> {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const endOfWeek = new Date(today);
      const daysUntilSunday = 7 - today.getDay();
      endOfWeek.setDate(today.getDate() + daysUntilSunday);
      endOfWeek.setHours(23, 59, 59, 999);
      
      const thirtyDaysFromNow = new Date(today);
      thirtyDaysFromNow.setDate(today.getDate() + 30);
      
      const result = await sql`
        WITH all_deadlines AS (
          -- Matter deadlines from matter_deadlines table
          SELECT due_date FROM matter_deadlines md
          JOIN matters m ON m.id = md.matter_id
          WHERE md.user_id = ${userId}
          AND md.is_completed = false
          UNION ALL
          -- Task deadlines
          SELECT deadline as due_date FROM tasks t
          WHERE t.user_id = ${userId}
          AND t.deadline IS NOT NULL
          AND t.status != 'Complete'
          AND (t.is_regular = false OR t.is_regular IS NULL)
          UNION ALL
          -- Goal/Deliverable deadlines (only if they have incomplete tasks)
          SELECT deadline as due_date FROM goals g
          WHERE g.user_id = ${userId}
          AND g.deadline IS NOT NULL
          AND g.archived = false
          AND EXISTS (
            SELECT 1 FROM tasks t 
            WHERE t.goal_id = g.id 
            AND t.status != 'Complete'
          )
          UNION ALL
          -- Project deadlines (only if they have deliverables with incomplete tasks)
          SELECT deadline as due_date FROM projects p
          WHERE p.user_id = ${userId}
          AND p.deadline IS NOT NULL
          AND p.is_archived = false
          AND EXISTS (
            SELECT 1 FROM goals g
            JOIN tasks t ON t.goal_id = g.id
            WHERE g.project_id = p.id 
            AND g.archived = false
            AND t.status != 'Complete'
          )
          UNION ALL
          -- Matter deadlines from matters table (only if they have deliverables with incomplete tasks)
          SELECT deadline as due_date FROM matters m
          WHERE m.user_id = ${userId}
          AND m.deadline IS NOT NULL
          AND m.is_archived = false
          AND EXISTS (
            SELECT 1 FROM goals g
            JOIN tasks t ON t.goal_id = g.id
            WHERE g.matter_id = m.id 
            AND g.archived = false
            AND t.status != 'Complete'
          )
        )
        SELECT 
          SUM(CASE WHEN due_date < ${today} THEN 1 ELSE 0 END) as overdue,
          SUM(CASE WHEN due_date >= ${today} AND due_date < ${tomorrow} THEN 1 ELSE 0 END) as today,
          SUM(CASE WHEN due_date >= ${today} AND due_date <= ${endOfWeek} THEN 1 ELSE 0 END) as this_week,
          SUM(CASE WHEN due_date >= ${today} AND due_date <= ${thirtyDaysFromNow} THEN 1 ELSE 0 END) as upcoming_30_days
        FROM all_deadlines
      `;
      
      return {
        overdue: parseInt(result[0]?.overdue || '0'),
        today: parseInt(result[0]?.today || '0'),
        thisWeek: parseInt(result[0]?.this_week || '0'),
        upcoming30Days: parseInt(result[0]?.upcoming_30_days || '0')
      };
    } catch (error) {
      console.error('Error fetching deadline stats:', error);
      return { overdue: 0, today: 0, thisWeek: 0, upcoming30Days: 0 };
    }
  }
};

export type DeadlinesModule = typeof deadlinesModule;

export function registerDeadlines(db: { deadlines?: DeadlinesModule }) {
  db.deadlines = deadlinesModule;
}

export { deadlinesModule as deadlines };
